import React, { useState } from 'react';
import { Checkbox } from '../../Components';
import { WalletApi, WalletHelpers } from '../api';
import type { AccountProps, ACHDetailProps, FocusPositionProps, MyPlayerProps } from '../../types';
import { useColors } from '../../constants/useColors';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { FlatList, KeyboardAvoidingView, Platform } from 'react-native';
const validator = require('us-bank-account-validator');

type ManualACHAddProps = {
    player:MyPlayerProps,
    account_id?:string,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    onUpdateAchDetails:(ach_detail:ACHDetailProps) => void,
    onAddAccount:(account:AccountProps) => void,
    onCancel:() => void
}

const ach_sections = ['header','name','bank_name','routing_number','account_number','disclaimer']
const ManualACHAdd = ({ player, account_id, onUpdateAchDetails, onAddAccount, onCancel, onFocusPosition }:ManualACHAddProps) => {
    const Colors = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string>();
    const [ ach_account, setACHAccount ] = useState({
        account: WalletHelpers.getEmptyACHAccount(),
        ach_details: WalletHelpers.getEmptyACHDetail()
    })
    const [ disclaimer, setDisclaimer ] = useState(false);

    const { account, ach_details } = ach_account;
    const bank_valid = ach_details.bank_name ? true : false;
    const routing_valid = validator.routingNumber(ach_details.routing_number);
    const account_valid = validator.accountNumber(ach_details.account_number);
    let valid = routing_valid && account_valid ? true : false
    if(!account_id && !bank_valid){ valid = false }
    if(!account_id && !disclaimer){ valid = false }
    
    const handleAddAccount = async() => {
        if(!valid || loading){ return }
        setLoading(true);
        try {
            const resp = await WalletApi.addACHAccount(account, ach_details)
            setACHAccount({ account: resp.account, ach_details: resp.ach_detail })
            onAddAccount({ ...resp.account, ach_detail: resp.ach_detail })
        } catch (e) {
            setError('Sorry, we were not able to add this account.  Please try again later.')
        }
        
        setLoading(false);
    }

    const handleAddAchDetails = async() => {
        if(!valid || loading || !account_id){ return }
        if(!ach_details.account_number || !ach_details.routing_number){ return }
        setLoading(true)
        const new_ach_detail = await WalletApi.addACHDetails(account_id, ach_details.routing_number, ach_details.account_number);
        if(!new_ach_detail){ setError('Sorry, we were not able to confirm this account.  Please try again.') }
        else { onUpdateAchDetails(new_ach_detail) }
        setLoading(false)
    }

    const renderSections = (data:{item:string,index:number}) => {
        switch(data.item){
            case 'header':
                if(account_id){
                    return (
                        <View type='header' style={{ padding:20 }}>
                            <Text theme='h1'>CONFIRM ACCOUNT DETAILS</Text>
                            <Text style={{ marginTop:4 }} theme='description'>Please confirm your account details below to continue.</Text>
                        </View>
                    )
                }
                return (
                    <View type='header' style={{ padding:20 }}>
                        <Text theme='h1'>MANUAL ACH ACCOUNT</Text>
                        <Text style={{ marginTop:4 }} theme='description'>Complete the form below to add a new ACH account.  Once submitted, we will send 2 small deposits to the account to confirm ownership. This process can take up to 3 business days.</Text>
                    </View>
                )
            case 'name':
                return (
                    <View style={{ padding:10 }}>
                        <Text size={14} theme='h2'>Name on account</Text>
                        <TextInput 
                            style={{ marginTop:10 }}
                            onFocusPosition={onFocusPosition}
                            value={`${player.first_name} ${player.last_name}`} 
                            editable={false}
                            onChangeText={() => console.log('ehy')}
                        />
                    </View>
                )
            case 'bank_name':
                if(account_id){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <Text size={14} theme='h2'>Bank Name</Text>
                        <TextInput 
                            style={{ marginTop:10 }}
                            onFocusPosition={onFocusPosition}
                            keyboardType='default' 
                            placeholder={'Bank Name'} 
                            value={ach_details.bank_name}
                            autoFocus={true} 
                            onChangeText={(e) => setACHAccount({ ...ach_account, ach_details: { ...ach_details, bank_name: e } })}
                        />
                        {!bank_valid ?
                        <Text style={{ marginTop:5 }} size={14} color={Colors.text.warning}>Bank Name Is Not Valid</Text>
                        :<></>}
                    </View>
                )
            case 'routing_number':
                return (
                    <View style={{ padding:10 }}>
                        <Text size={14} theme='h2'>Routing Number</Text>
                        <TextInput 
                            style={{ marginTop:10 }}
                            onFocusPosition={onFocusPosition}
                            keyboardType='number-pad'
                            placeholder={'xxxxxx911'}
                            value={ach_details.routing_number}
                            onChangeText={(e) => setACHAccount({ ...ach_account, ach_details: { ...ach_details, routing_number: e } })}
                        />
                        {!routing_valid.isValid ?
                        <Text style={{ marginTop:5 }} size={14} color={Colors.text.warning}>Routing Number Is Not Valid</Text>
                        :<></>}
                    </View>
                )
            case 'account_number':
                return (
                    <View style={{ padding:10, paddingBottom:150 }}>
                        <Text size={14} theme='h2'>Account Number</Text>
                        <TextInput 
                            style={{ marginTop:10 }}
                            onFocusPosition={onFocusPosition}
                            keyboardType='number-pad'
                            value={ach_details.account_number}
                            placeholder={'xxxxxxxxx0000'}
                            onChangeText={(e) => setACHAccount({ ...ach_account, ach_details: { ...ach_details, account_number: e } })}
                        />
                        {!account_valid.isValid ?
                        <Text style={{ marginTop:5 }} size={14} color={Colors.text.warning}>Account Number Is Not Valid</Text>
                        :<></>}
                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <KeyboardAvoidingView style={{ flex:1 }} behavior={Platform.OS == 'ios' ? 'padding' : 'height'}>
                <FlatList
                    key='manual_ach_sections'
                    keyExtractor={(item) => item}
                    data={ach_sections}
                    renderItem={renderSections}
                />
            </KeyboardAvoidingView>
            {!account_id ?
            <View>
                <Button style={{ flexDirection:'row', alignItems:'center', paddingLeft:20, paddingRight:20 }} onPress={() => setDisclaimer(!disclaimer)}>
                    <Checkbox size={30} checked={disclaimer} disabled onSelect={() => console.log('')}/>
                    <Text style={{ marginLeft:10 }} size={14} theme='description'>I understand that by continuing, we will send 2 small deposits to this bank account to confirm ownership. Once received, I will enter the amounts here.</Text>
                </Button>
            </View>
            :<></>}
            {error?
            <View style={{ padding:10 }}>
                <Text size={16} color={Colors.text.warning} textAlign='center' weight='semibold'>{error}</Text>
            </View>
            :<></>}
            {account.status == 'pending' ?
            <View style={{ flexDirection:'row', padding:20 }}>
                <Button
                    title='CANCEL'
                    style={{ flex:1, marginRight:5, opacity:loading?0.5:1 }}
                    disabled={loading}
                    type='error'
                    onPress={() => onCancel()}
                />
                <Button
                    title={account_id? 'CONFIRM ACCOUNT': 'ADD ACCOUNT'}
                    style={{ flex:3, opacity:valid&&!loading&&!error?1:0.5 }}
                    disabled={!valid || loading || error?true:false}
                    loading={loading}
                    type='success'
                    onPress={() => account_id ? handleAddAchDetails() : handleAddAccount()}
                />
            </View>
            : account.status == 'verify' ?
            <View style={{ flexDirection:'row', padding:20 }}>
                <Button
                    title='CLOSE'
                    style={{ flex:1, marginRight:5 }}
                    type='error'
                    onPress={() => onCancel()}
                />
                <Button
                    title='VERIFY TRANSACTIONS SENT'
                    style={{ flex:3 }}
                    disabled
                    backgroundColor={Colors.absolutes.brand.slate}
                    onPress={() => console.log('')}
                />
            </View>
            :<></>}
        </View>
    )
}

export default ManualACHAdd